import {
  db,
  addBodyEntry,
  addSet,
  addWarmupSets,
  advanceRotation,
  discardSession,
  exportBackup,
  finishSession,
  getActiveSession,
  getProgressPhotos,
  importBackup,
  initializeDatabase,
  removeSet,
  restoreSet,
  saveDeviceConfig,
  saveSettings,
  startSession,
  updateHistoricalSession,
  updateSet
} from './db';
import { computeMetrics, evaluatePr, formatWeight, nextSessionTarget, prLabels, stalledExercises, summarizeSession, warmupPlan } from './metrics';
import { adjustedRestDeadline, restAlarmDue } from './rest';
import { requestScreenWakeLock } from './wake-lock';
import type { AppMetrics, AppRoute, AppSettings, BodyLog, DeviceConfig, Exercise, ProgressPhoto, Routine, Session, SessionSummary, WorkoutSet } from './types';

const emptyMetrics: AppMetrics = { completedSessions: 0, weeklySessions: 0, weeklySets: 0, weeklyVolume: 0, streak: 0, prCount: 0 };
const routes: AppRoute[] = ['today', 'week', 'calendar', 'workout', 'progress', 'library', 'settings'];

export const app = $state({
  ready: false,
  route: 'today' as AppRoute,
  routines: [] as Routine[],
  exercises: [] as Exercise[],
  sessions: [] as Session[],
  sets: [] as WorkoutSet[],
  bodyLog: [] as BodyLog[],
  settings: undefined as AppSettings | undefined,
  deviceConfig: undefined as DeviceConfig | undefined,
  activeSession: undefined as Session | undefined,
  activeSets: [] as WorkoutSet[],
  metrics: emptyMetrics,
  bodyWeight: 0,
  now: Date.now(),
  toast: '',
  toastAction: '' as '' | 'undo',
  lastDeletedSet: undefined as WorkoutSet | undefined,
  lastSummary: undefined as SessionSummary | undefined,
  zeroSetFinishPrompt: false,
  prMessage: '',
  updateReady: false,
  installing: false,
  syncStatus: 'Local-first storage ready' as string,
  coachInsight: '' as string,
  coachSource: 'Local rule' as 'Local rule' | 'Coach (AI)',
  pushSupported: typeof window !== 'undefined' && 'Notification' in window && 'PushManager' in window
});

let toastTimer: number | undefined;
let undoTimer: number | undefined;
let clockTimer: number | undefined;
let restGraceTimer: number | undefined;
let syncTimer: number | undefined;
let wakeLock: WakeLockSentinel | undefined;
let audioContext: AudioContext | undefined;
let restAlarmPlayedFor = 0;
let celebratedSetIds = new Set<string>();
let celebratedPrKeys = new Set<string>();

export function showToast(message: string, action: '' | 'undo' = '', duration = 2600): void {
  window.clearTimeout(toastTimer);
  app.toast = message;
  app.toastAction = action;
  toastTimer = window.setTimeout(() => {
    app.toast = '';
    app.toastAction = '';
  }, duration);
}

function latestBodyWeight(bodyLog: BodyLog[], settings?: AppSettings): number {
  return bodyLog.filter(entry => Number(entry.weight) > 0).sort((a, b) => b.date.localeCompare(a.date))[0]?.weight || settings?.bodyWeightLb || 0;
}

function recomputeMetrics(): void {
  app.bodyWeight = latestBodyWeight(app.bodyLog, app.settings);
  app.metrics = computeMetrics(app.sessions, app.sets, app.now, app.exercises, app.routines, app.settings, app.bodyWeight);
}

function setActiveSession(next: Session | undefined): void {
  app.activeSession = next;
  if (next) {
    const index = app.sessions.findIndex(session => session.id === next.id);
    if (index >= 0) app.sessions[index] = next;
  }
  manageClock();
}

export async function refreshApp(): Promise<void> {
  const [routines, exercises, sessions, sets, bodyLog, settings, deviceConfig, activeSession] = await Promise.all([
    db.routines.orderBy('dayOfCycle').toArray(),
    db.exercises.orderBy('name').toArray(),
    db.sessions.orderBy('startedAt').reverse().toArray(),
    db.sets.toArray(),
    db.bodyLog.orderBy('date').reverse().toArray(),
    db.settings.get('primary'),
    db.deviceConfig.get('device'),
    getActiveSession()
  ]);
  app.routines = routines;
  app.exercises = exercises;
  app.sessions = sessions;
  app.sets = sets;
  app.bodyLog = bodyLog;
  app.settings = settings;
  app.deviceConfig = deviceConfig;
  app.activeSession = activeSession;
  app.activeSets = activeSession ? sets.filter(set => set.sessionId === activeSession.id).sort((a, b) => a.exerciseId.localeCompare(b.exerciseId) || a.setNumber - b.setNumber) : [];
  recomputeMetrics();
  manageClock();
}

export async function initializeApp(): Promise<void> {
  await initializeDatabase();
  const hash = location.hash.replace('#/', '') as AppRoute;
  if (routes.includes(hash)) app.route = hash;
  await refreshApp();
  if (app.activeSession?.restEndsAt && app.activeSession.restEndsAt <= Date.now()) await skipRest();
  app.ready = true;
  if (app.activeSession) void acquireWakeLock();
  window.addEventListener('hashchange', handleHashChange);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('online', handleOnline);
  scheduleAutomaticSync();
}

function handleHashChange(): void {
  const next = location.hash.replace('#/', '') as AppRoute;
  if (routes.includes(next)) app.route = next;
}

function handleVisibilityChange(): void {
  if (document.visibilityState === 'visible' && app.activeSession) void acquireWakeLock();
}

function handleOnline(): void {
  scheduleAutomaticSync();
}

function scheduleAutomaticSync(): void {
  window.clearTimeout(syncTimer);
  if (!app.deviceConfig?.syncApiUrl || !app.deviceConfig.syncToken) return;
  syncTimer = window.setTimeout(() => void syncApplication(true), 30000);
}

function manageClock(): void {
  if (app.activeSession && !clockTimer) clockTimer = window.setInterval(tickClock, 250);
  if (!app.activeSession && clockTimer) {
    window.clearInterval(clockTimer);
    clockTimer = undefined;
  }
}

function tickClock(): void {
  app.now = Date.now();
  const end = app.activeSession?.restEndsAt;
  if (!restAlarmDue(end, restAlarmPlayedFor, app.now)) return;
  restAlarmPlayedFor = end!;
  if (app.settings?.soundHaptics !== false) {
    playRestChime();
    feedback('rest');
  }
  showToast('Rest complete. Your next set is ready.', '', 4200);
  window.clearTimeout(restGraceTimer);
  restGraceTimer = window.setTimeout(async () => {
    if (app.activeSession?.restEndsAt !== end) return;
    await skipRest();
  }, 5000);
}

export function navigate(route: AppRoute): void {
  const change = () => {
    app.route = route;
    location.hash = `/${route}`;
    window.scrollTo({ top: 0, behavior: 'instant' });
  };
  const transitions = document as Document & { startViewTransition?: (callback: () => void) => unknown };
  if (transitions.startViewTransition && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) transitions.startViewTransition(change);
  else change();
}

export function routineForSession(session: Session | undefined): Routine | undefined {
  return session ? app.routines.find(routine => routine.id === session.routineId) : undefined;
}

export function exerciseById(id: string): Exercise | undefined {
  return app.exercises.find(exercise => exercise.id === id);
}

export async function beginRoutine(routineId: string): Promise<void> {
  try {
    await unlockAudio();
    await startSession(routineId);
    celebratedSetIds.clear();
    celebratedPrKeys.clear();
    await refreshApp();
    await acquireWakeLock();
    navigate('workout');
    showToast('Workout started. Every change saves automatically.');
  } catch (error) {
    showToast(error instanceof Error ? error.message : 'Could not start this workout.');
  }
}

export async function saveSetValue(set: WorkoutSet, changes: Partial<WorkoutSet>): Promise<void> {
  const current = app.sets.find(item => item.id === set.id);
  if (!current) return;
  const updatedAt = Date.now();
  Object.assign(current, changes, { updatedAt });
  const active = app.activeSets.find(item => item.id === set.id);
  if (active && active !== current) Object.assign(active, changes, { updatedAt });
  recomputeMetrics();
  await updateSet(set.id, changes);
}

export async function stepSetValue(setId: string, field: 'weight' | 'reps', delta: number, minimum: number, maximum: number): Promise<void> {
  const current = app.sets.find(set => set.id === setId);
  if (!current) return;
  const value = Math.min(maximum, Math.max(minimum, current[field] + delta));
  await saveSetValue(current, { [field]: value });
}

export async function toggleSetComplete(set: WorkoutSet): Promise<void> {
  await unlockAudio();
  const current = app.sets.find(item => item.id === set.id);
  if (!current) return;
  if (current.completedAt) {
    await saveSetValue(current, { completedAt: undefined });
    if (app.activeSession?.restSetId === current.id) await skipRest();
    return;
  }
  const completedAt = Date.now();
  const candidate = { ...current, completedAt };
  const exercise = exerciseById(current.exerciseId);
  const history = app.sets.filter(item => item.id !== current.id && item.completedAt && (item.sessionId !== app.activeSession?.id || (item.completedAt || 0) < completedAt));
  const result = evaluatePr(candidate, history, exercise, app.bodyWeight);
  await saveSetValue(current, { completedAt });
  if (exercise && app.activeSession) {
    const routine = routineForSession(app.activeSession);
    const reference = routine?.exerciseRefs.find(item => item.exerciseId === exercise.id);
    const group = reference?.supersetGroup;
    const groupRefs = group ? routine?.exerciseRefs.filter(item => item.supersetGroup === group) || [] : [];
    const groupIndex = groupRefs.findIndex(item => item.exerciseId === exercise.id);
    const finalPartner = groupRefs.length > 1 && groupIndex === groupRefs.length - 1;
    if (!group || finalPartner) await startRest(reference?.restSeconds || exercise.restSeconds, exercise.id, current.id);
    else if (app.activeSession.restEndsAt) await skipRest();
    if (groupRefs.length > 1) {
      const partner = groupRefs[(groupIndex + 1) % groupRefs.length];
      window.setTimeout(() => document.getElementById(`exercise-${partner.exerciseId}`)?.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }), 40);
    }
  }
  if (app.settings?.soundHaptics !== false) feedback('set');
  if (!celebratedSetIds.has(current.id)) {
    celebratedSetIds.add(current.id);
    const labels = prLabels(result).filter(label => {
      const key = `${current.exerciseId}:${label}`;
      if (celebratedPrKeys.has(key)) return false;
      celebratedPrKeys.add(key);
      return true;
    });
    if (labels.length) {
      app.prMessage = `${labels.join(' + ')} on ${exercise?.name || 'this exercise'}`;
      showToast(app.prMessage, '', 4200);
      window.setTimeout(() => (app.prMessage = ''), 4400);
    }
  }
}

export async function appendSet(exerciseId: string): Promise<void> {
  if (!app.activeSession) return;
  if (app.activeSets.filter(set => set.exerciseId === exerciseId).length >= 12) return showToast('This exercise is limited to 12 sets.');
  const set = await addSet(app.activeSession.id, exerciseId);
  app.sets.push(set);
  app.activeSets.push(set);
}

export async function appendWarmups(exerciseId: string, workingWeight: number): Promise<void> {
  if (!app.activeSession || !app.settings) return;
  const exercise = exerciseById(exerciseId);
  if (!exercise || exercise.loadType !== 'external') return;
  const plan = warmupPlan(workingWeight, app.settings.barWeight, app.settings.plateInventory);
  if (!plan.length) return showToast('Warm-ups appear when the first working set reaches 95 lb.');
  if (app.activeSets.some(set => set.exerciseId === exerciseId && set.type === 'warmup')) return showToast('Warm-up sets are already added.');
  await addWarmupSets(app.activeSession.id, exerciseId, plan);
  await refreshActiveSetsOnly();
  showToast('Four plate-ready warm-up sets added.');
}

export async function deleteSet(set: WorkoutSet): Promise<void> {
  if (app.activeSets.filter(item => item.exerciseId === set.exerciseId).length <= 1) return showToast('Keep at least one set for this exercise.');
  app.lastDeletedSet = { ...set };
  if (app.activeSession?.restSetId === set.id) await skipRest();
  await removeSet(set);
  await refreshActiveSetsOnly();
  window.clearTimeout(undoTimer);
  showToast('Set removed.', 'undo', 5000);
  undoTimer = window.setTimeout(() => (app.lastDeletedSet = undefined), 5000);
}

async function refreshActiveSetsOnly(): Promise<void> {
  if (!app.activeSession) return;
  const values = await db.sets.where('sessionId').equals(app.activeSession.id).toArray();
  app.sets = [...app.sets.filter(set => set.sessionId !== app.activeSession!.id), ...values];
  app.activeSets = values;
  recomputeMetrics();
}

export async function undoDelete(): Promise<void> {
  if (!app.lastDeletedSet) return;
  await restoreSet(app.lastDeletedSet);
  app.lastDeletedSet = undefined;
  await refreshActiveSetsOnly();
  showToast('Set restored.');
}

export async function startRest(seconds: number, exerciseId?: string, setId?: string): Promise<void> {
  if (!app.activeSession) return;
  const next = { ...app.activeSession, restEndsAt: Date.now() + Math.max(0, seconds) * 1000, restExerciseId: exerciseId, restSetId: setId, updatedAt: Date.now() };
  restAlarmPlayedFor = 0;
  setActiveSession(next);
  await db.sessions.update(next.id, { restEndsAt: next.restEndsAt, restExerciseId: exerciseId, restSetId: setId, updatedAt: next.updatedAt });
  if (next.restEndsAt) void scheduleRestPush(next.restEndsAt, exerciseId, setId);
}

export async function skipRest(): Promise<void> {
  if (!app.activeSession) return;
  const next = { ...app.activeSession, restEndsAt: undefined, restExerciseId: undefined, restSetId: undefined, updatedAt: Date.now() };
  window.clearTimeout(restGraceTimer);
  setActiveSession(next);
  await db.sessions.update(next.id, { restEndsAt: undefined, restExerciseId: undefined, restSetId: undefined, updatedAt: next.updatedAt });
  void cancelRestPush();
}

export async function adjustRest(deltaSeconds: number): Promise<void> {
  if (!app.activeSession) return;
  const deadline = adjustedRestDeadline(app.activeSession.restEndsAt, deltaSeconds);
  if (!deadline) return;
  restAlarmPlayedFor = 0;
  const next = { ...app.activeSession, restEndsAt: deadline, updatedAt: Date.now() };
  setActiveSession(next);
  await db.sessions.update(next.id, { restEndsAt: deadline, updatedAt: next.updatedAt });
}

export async function completeWorkout(notes = ''): Promise<void> {
  if (!app.activeSession) return;
  const completed = app.activeSets.filter(set => set.completedAt);
  if (!completed.length) {
    app.zeroSetFinishPrompt = true;
    return;
  }
  const current = app.activeSession;
  const routine = routineForSession(current);
  const finished = await finishSession(current.id, notes);
  app.lastSummary = summarizeSession(finished, app.sets, app.sets, app.exercises, routine?.name || 'Workout', app.bodyWeight);
  celebratedSetIds.clear();
  celebratedPrKeys.clear();
  app.zeroSetFinishPrompt = false;
  await releaseWakeLock();
  await refreshApp();
  navigate('today');
  scheduleAutomaticSync();
  showToast('Workout saved to your permanent history.', '', 3800);
}

export async function cancelWorkout(): Promise<void> {
  if (!app.activeSession) return;
  await discardSession(app.activeSession.id);
  celebratedSetIds.clear();
  celebratedPrKeys.clear();
  app.zeroSetFinishPrompt = false;
  await releaseWakeLock();
  await refreshApp();
  navigate('today');
  showToast('Unfinished workout discarded.');
}

export async function skipPlannedRest(): Promise<void> {
  await advanceRotation();
  await refreshApp();
  showToast(`Rotation advanced to Day ${app.settings?.nextRoutineDay}.`);
}

export function dismissSummary(): void {
  app.lastSummary = undefined;
}

export function previousSet(exerciseId: string, setNumber: number): WorkoutSet | undefined {
  return app.sets.filter(set => set.exerciseId === exerciseId && set.setNumber === setNumber && set.sessionId !== app.activeSession?.id && Boolean(set.completedAt)).sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0))[0];
}

export async function persistRoutine(routine: Routine): Promise<void> {
  const plain = JSON.parse(JSON.stringify({ ...routine, updatedAt: Date.now() })) as Routine;
  await db.routines.put(plain);
  await refreshApp();
  showToast('Routine saved.');
}

export async function persistExercise(exercise: Exercise): Promise<void> {
  await db.exercises.put({ ...exercise, updatedAt: Date.now() });
  await refreshApp();
  showToast('Exercise saved to your library.');
}

export async function logBody(entry: Omit<BodyLog, 'id' | 'updatedAt'>, photo?: Blob): Promise<void> {
  await addBodyEntry(entry, photo);
  await refreshApp();
  scheduleAutomaticSync();
  showToast('Body log saved.');
}

export async function loadProgressPhotos(ids?: string[]): Promise<ProgressPhoto[]> {
  return getProgressPhotos(ids);
}

export async function downloadBackup(): Promise<void> {
  const blob = await exportBackup();
  const filename = `sampson-swole-backup-${new Date().toISOString().slice(0, 10)}.zip`;
  const file = new File([blob], filename, { type: 'application/zip' });
  if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], title: 'Sampson Swole backup' });
  else {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  }
  showToast('Backup created.');
}

export async function restoreBackup(file: File): Promise<void> {
  await importBackup(file);
  await refreshApp();
  showToast('Backup restored successfully.');
}

export async function updateSettings(changes: Partial<AppSettings>): Promise<void> {
  app.settings = await saveSettings(changes);
  recomputeMetrics();
}

export async function updateDeviceConfig(changes: Partial<DeviceConfig>): Promise<void> {
  app.deviceConfig = await saveDeviceConfig(changes);
}

export async function enableRestAlerts(): Promise<void> {
  if (!app.pushSupported) return showToast('Locked-screen rest alerts are not supported in this browser.');
  if (!app.deviceConfig?.syncApiUrl || !app.deviceConfig.syncToken) return showToast('Connect private cloud sync before enabling rest alerts.', '', 4200);
  try {
    const { subscribeToRestAlerts } = await import('./push');
    await subscribeToRestAlerts(app.deviceConfig);
    await updateDeviceConfig({ pushEnabled: true });
    showToast('Locked-screen rest alerts are ready.');
  } catch (error) {
    showToast(error instanceof Error ? error.message : 'Rest alerts could not be enabled.', '', 4500);
  }
}

async function scheduleRestPush(deadline: number, exerciseId?: string, setId?: string): Promise<void> {
  if (!app.deviceConfig?.pushEnabled || !app.deviceConfig.syncApiUrl || !app.deviceConfig.syncToken) return;
  const exerciseName = exerciseById(exerciseId || '')?.name || 'Next exercise';
  const setNumber = app.sets.find(set => set.id === setId)?.setNumber || 1;
  try {
    const { scheduleRestAlert } = await import('./push');
    await scheduleRestAlert(app.deviceConfig, deadline, exerciseName, setNumber);
  } catch {
    // The in-app rest timer remains authoritative if push scheduling is unavailable.
  }
}

async function cancelRestPush(): Promise<void> {
  if (!app.deviceConfig?.pushEnabled || !app.deviceConfig.syncApiUrl || !app.deviceConfig.syncToken) return;
  try {
    const { cancelRestAlert } = await import('./push');
    await cancelRestAlert(app.deviceConfig);
  } catch {
    // A short-lived stale push is safer than interrupting the local logger.
  }
}

export async function editHistorySession(sessionId: string, value: string, notes = ''): Promise<void> {
  await updateHistoricalSession(sessionId, { localDate: value, notes });
  await refreshApp();
  showToast('Workout history updated.');
}

export async function deleteHistorySession(sessionId: string): Promise<void> {
  await discardSession(sessionId);
  await refreshApp();
  showToast('Workout removed from history.');
}

export async function syncApplication(silent = false): Promise<void> {
  if (!app.deviceConfig) return;
  app.syncStatus = 'Syncing private records...';
  try {
    const { synchronize } = await import('./sync');
    const result = await synchronize(app.deviceConfig);
    await refreshApp();
    app.syncStatus = `Synced ${result.uploaded} up and ${result.downloaded} down`;
    if (!silent) showToast('Cloud sync completed.');
  } catch (error) {
    app.syncStatus = error instanceof Error ? error.message : 'Cloud sync failed.';
    if (!silent) showToast(app.syncStatus, '', 4800);
  }
}

export async function refreshCoach(): Promise<void> {
  if (!app.deviceConfig?.syncApiUrl || !app.deviceConfig.syncToken) {
    app.coachSource = 'Local rule';
    app.coachInsight = localCoachInsight();
    return;
  }
  app.coachInsight = 'Reviewing your saved training summary...';
  try {
    const { askCoach } = await import('./sync');
    const stalled = stalledExercises(app.sessions, app.sets, app.exercises, app.bodyWeight).map(id => exerciseById(id)?.name).filter(Boolean);
    const result = await askCoach(app.deviceConfig, { metrics: app.metrics, recentSessions: app.sessions.filter(session => session.finishedAt).slice(0, 8), recentSets: app.sets.filter(set => set.completedAt).slice(-80), stalledExercises: stalled });
    app.coachInsight = result.insight;
    app.coachSource = result.source === 'claude' ? 'Coach (AI)' : 'Local rule';
  } catch {
    app.coachSource = 'Local rule';
    app.coachInsight = localCoachInsight();
  }
}

function localCoachInsight(): string {
  const session = app.sessions.filter(item => item.finishedAt).sort((a, b) => (b.finishedAt || 0) - (a.finishedAt || 0))[0];
  const routine = session ? app.routines.find(item => item.id === session.routineId) : undefined;
  if (!session || !routine) return 'Complete your next scheduled workout and log every working set. Your first target is consistency.';
  const stalledId = stalledExercises(app.sessions, app.sets, app.exercises, app.bodyWeight)[0];
  const stalledExercise = exerciseById(stalledId || '');
  if (stalledExercise) return `${stalledExercise.name}: deload 10 percent for the next session. Three completed sessions passed without an estimated 1RM increase.`;
  for (const reference of routine.exerciseRefs) {
    const exercise = exerciseById(reference.exerciseId);
    const target = nextSessionTarget(app.sets.filter(set => set.sessionId === session.id && set.exerciseId === reference.exerciseId), reference.repTarget.max, app.settings?.microPlates ? 2.5 : 5);
    if (!exercise || !target) continue;
    const load = exercise.loadType === 'bodyweight' ? `BW${target.weight ? `+${formatWeight(target.weight)}` : ''}` : `${formatWeight(target.weight)} lb`;
    return `${exercise.name}: target ${load} for ${target.reps} reps. ${target.rationale}`;
  }
  return 'Keep the current load until every set reaches the top of the rep range, then add the smallest available increment.';
}

async function unlockAudio(): Promise<void> {
  if (!audioContext) audioContext = new AudioContext();
  if (audioContext.state === 'suspended') await audioContext.resume();
}

function playRestChime(): void {
  if (!audioContext) return;
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(740, audioContext.currentTime);
  oscillator.frequency.exponentialRampToValueAtTime(980, audioContext.currentTime + 0.28);
  gain.gain.setValueAtTime(0.0001, audioContext.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.22, audioContext.currentTime + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.42);
  oscillator.connect(gain).connect(audioContext.destination);
  oscillator.start();
  oscillator.stop(audioContext.currentTime + 0.45);
}

export function feedback(kind: 'set' | 'rest'): void {
  navigator.vibrate?.(kind === 'rest' ? [200, 100, 200] : 10);
  if (kind === 'set' || kind === 'rest') (document.getElementById('haptic-switch') as HTMLInputElement | null)?.click();
}

export async function acquireWakeLock(): Promise<void> {
  try {
    wakeLock = await requestScreenWakeLock(navigator.wakeLock, document.visibilityState === 'visible');
  } catch {
    showToast('Keep the screen awake manually on this device.');
  }
}

export async function releaseWakeLock(): Promise<void> {
  try {
    await wakeLock?.release();
  } finally {
    wakeLock = undefined;
  }
}

export function disposeAppState(): void {
  window.clearInterval(clockTimer);
  window.clearTimeout(restGraceTimer);
  window.clearTimeout(syncTimer);
  window.removeEventListener('hashchange', handleHashChange);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('online', handleOnline);
  void releaseWakeLock();
}
